import { useEffect, useRef } from "react";
import { useRouter } from "next/router";

/**
 * Closes an openable overlay when the Next.js route changes.
 * Listens to `routeChangeStart` and also reacts to `asPath` changes so soft
 * navigations (sidebar, menu links) cannot leave a transparent full-viewport
 * backdrop or a stuck hover submenu mounted.
 */
export function useCloseOpenableOnRouteChange(isOpen: boolean, close: () => void): void {
	const router = useRouter();
	const asPath = router.asPath;
	const prevAsPathRef = useRef(asPath);

	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}
		const handleRouteChangeStart = (): void => {
			close();
		};
		router.events.on("routeChangeStart", handleRouteChangeStart);
		return (): void => {
			router.events.off("routeChangeStart", handleRouteChangeStart);
		};
	}, [close, isOpen, router.events]);

	useEffect(() => {
		if (prevAsPathRef.current === asPath) {
			return;
		}
		prevAsPathRef.current = asPath;
		if (isOpen) {
			close();
		}
	}, [asPath, close, isOpen]);
}
